import * as React from "react";
import { Grid, Paper, Typography, Button, Stack, Box, Divider, Link, Chip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import TvIcon from "@mui/icons-material/Tv";
import CachedIcon from "@mui/icons-material/Cached";
import CheckIcon from "@mui/icons-material/Check";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import ActivityHeading from "../../../Components/Activity/ActivityHeading/activity-heading";
import ActivityContext from "../../../Components/Activity/ActivityContext/activity-context";
import ActivityDetails from "../../../Components/Activity/AcitivityDetails/activity-details";
import ActivityComment from "../../../Components/Activity/AcitivityDetails/activity-comment";
import ActivityError from "../../../Components/Activity/AcitivityDetails/activity-error";
import ActivityField from "../../../Components/Activity/AcitivityDetails/activity-field";
import ActivityUpdate from "../../../Components/Activity/AcitivityDetails/activity-update";
import ActivityCommentFeed from "../../../Components/Activity/AcitivityDetails/activity-comment-feed";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import EmptyListSlot from "../../../Components/Misc/EmptyListSlot";
import { ModalContext } from "../../../Components/Modals";
import ModalRenameCollection, {
  ModalRenameElementProps,
} from "../../../Components/Modals/ModalRenameCollection/modal-rename-collection";
import OrderStatusIcon from "../../../Components/Misc/Status/OrderStatus/order-status";
import HelpPopup from "../../../Components/HelpPopup";
import { TutorialContext } from "../../../Utils/Helpers/tutorial";
import AssetStatus from "../../../Components/Misc/Status/AssetStatus/asset-status";
import ShareDetailsPanel from "../../../Components/Panels/ShareDetailsPanel/share-panel";
import PageFilter from "../../../Components/Misc/PageFilter";
import CommentForm from "../../../Components/Misc/CommentForm/comment-form";
import PrioritySelect from "../../../Components/Misc/PrioritySelect";

import CastingPanel from "../../../Components/Panels/CastingPanel/casting-panel";
import OrderItemPanel from "../../../Components/Panels/OrderItemPanel/order-item-panel";
import OrderPanel from "../../../Components/Panels/OrderPanel/order-panel";
import SynopsisPanel from "../../../Components/Panels/SynopsisPanel/syno-panel";
import NewRequestPanel from "../../../Components/Panels/NewRequestPanels/new-request-panel";
import FilterAccordion from "../../../Components/Filters/FilterAccordion/filters-accordion";
import ConfigContentRecaps from "../../../Components/Config/ConfigContentRecaps/config-content-row-recap";
import TitleItem from "../../../Components/Lists/TitleItemList/TitleItem";
import AssetTypeChip from "../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import PanelHeader from "../../../Components/Panels/PanelHeader/panel-header";
import Note from "../../../Components/Misc/Note/note";
import ActivityConversation from "../../../Components/Activity/ActivityConversation/activity-conversation";
import BoxPaper from "../../../Components/Misc/BoxPaper";
import AdvancedQuerySaved from "../../../Components/AdvancedQuery/AdvancedQuerySaved/advanced-query-saved";
import AdvancedQueryTag from "../../../Components/AdvancedQuery/AdvancedQueryTag/advanced-query-tag";
import AdvancedQueries from "../../../Components/AdvancedQuery/AdvancedQueries/advanced-queries";
import ProvidersDetailPanel from "../../../Components/Panels/ProvidersDetailPanel/providers-detail-panel";
import CustomerDetailsPanel from "../../../Components/Panels/CustomerDetailsPanel/customer-panel";
import CustomerUsersPanel from "../../../Components/Panels/CustomerUsersPanel/customer-users-panel";
import CustomerEditUserPanel from "../../../Components/Panels/CustomerEditUserPanel/customer-edit-user-panel";
import MetadataListTextItem from "../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import MetadataListItem from "../../../Components/Misc/Metadata/MetadataListItem";
import MetadataListChipListItem from "../../../Components/Misc/Metadata/MetadataListChipListItem";
import MetadataBoxAccordion from "../../../Components/Misc/Metadata/MetadataBoxAccordion";
import EditUserRolePanel from "../../../Components/Panels/EditUserRolePanel/edit-user-role-panel";
import GroupUserPanel from "../../../Components/Panels/GroupUserPanel/group-user-panel";
import ModalOnboarding from "../../../Components/Modals/ModalOnboarding";
import { OrderStatus as OrderStatusEnum } from "../../../Core/Api/Types/order-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import ModalUploadMediaFiles from "../../../Components/Modals/ModalUploadMediaFiles/modal-upload-media-files";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import AssetPanel from "../../../Components/Panels/AssetPanel/asset-panel";
import { useTranslation } from "react-i18next";

function DemoSamples() {
  const { t } = useTranslation();
  const { active: treeViewPanelOpen, toggle: toggleTreeViewPanel, unactivate: closeTreeViewPanel } = useToggle(false);

  const [helpPopupAnchorEl, setHelpPopupAnchorEl] = React.useState<HTMLElement | null>(null);

  const { active: processingAssetPanelOpen, toggle: toggleProcessingAssetPanel } = useToggle(false);
  const { active: rejectedAssetPanelOpen, toggle: toggleRejectedAssetPanelOpen } = useToggle(false);
  const { active: shareDetailsPanelOpen, toggle: toggleShareDetailsPanelOpen } = useToggle(false);
  const { active: castingPanelOpen, toggle: toggleCastingPanelOpen } = useToggle(false);
  const { active: configPanelOpen, toggle: toggleConfigPanelOpen } = useToggle(false);
  const { active: orderPanelOpen, toggle: toggleOrderPanelOpen } = useToggle(false);
  const { active: synopsisPanelOpen, toggle: toggleSynopsisPanelOpen } = useToggle(false);
  const { active: newRequestPanelOpen, toggle: toggleNewRequestPanel } = useToggle(false);
  const { active: providerDetailsPanelOpen, toggle: toggleProviderDetailsPanel } = useToggle(false);
  const { active: customerDetailsPanelOpen, toggle: toggleCustomerDetailsPanel } = useToggle(false);
  const { active: customerEditUserPanelOpen, toggle: toggleCustomerEditUserPanel } = useToggle(false);
  const { active: customerUsersPanel, toggle: toggleCustomerUsersPanel } = useToggle(false);
  const { active: editUserRolePanelOpen, toggle: toggleEditUserRolePanel } = useToggle(false);
  const { active: groupUserPanelOpen, toggle: toggleGroupUserPanel } = useToggle(false);

  const [priority, setPriority] = React.useState<"low" | "medium" | "high" | "urgent">("low");
  const tutorialContext = React.useContext(TutorialContext);
  const { setTutorialPage } = { ...tutorialContext };

  const handleHelpClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    setHelpPopupAnchorEl(e.currentTarget);
  };

  const onClosePopup = () => {
    setHelpPopupAnchorEl(null);
  };

  React.useEffect(() => {
    if (!setTutorialPage) {
      return;
    }

    setTutorialPage("demo");
  }, [setTutorialPage]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none", bgcolor: "default" }}
          >
            <h1 id="status">{t("common:status")}</h1>
            <h2 id="status-order">{t("common:order")}</h2>
            <Stack direction="row" spacing={1}>
              <OrderStatusIcon status={OrderStatusEnum.RUNNING} />
              <OrderStatusIcon status={OrderStatusEnum.QUEUED} />
              <OrderStatusIcon status={OrderStatusEnum.FAILED} />
              <OrderStatusIcon status={OrderStatusEnum.COMPLETED} />
              <OrderStatusIcon status={OrderStatusEnum.FAILED} />
            </Stack>
            <h2 id="status-assets">{t("common:assets")}</h2>
            <Stack direction="row" spacing={1}>
              <AssetStatus status={ObjectStatus.Accepted} />
              <AssetStatus status={ObjectStatus.New} />
              <AssetStatus status={ObjectStatus.New} />
              <AssetStatus status={ObjectStatus.New} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper sx={{ mt: 4, mb: 4, p: 2, backgroundImage: "none" }}>
            <h1 id="metadata-list-items">{t("page:body.metadataListItem")}</h1>
            <Stack spacing={1}>
              <MetadataListTextItem title={t("common:icon")}>
                <CheckCircleOutlinedIcon fontSize="small" color="success" />
              </MetadataListTextItem>
              <MetadataListTextItem title={`${t("common:icon")} + ${t("common:lowerCaseText")}`} color="error.main">
                <>
                  <ErrorOutlineOutlinedIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "text-bottom" }} />
                  {t("common:text")}
                </>
              </MetadataListTextItem>
              <MetadataListTextItem title={t("common:longText")} wrapperProps={{ alignItems: "flex-start" }}>
                Lorem ipsum dolor sit, amet consectetur adipisicing elit. Assumenda mollitia fugiat obcaecati, optio
                aperiam neque iusto voluptates porro consequuntur illo? Perferendis alias aperiam distinctio sed
                voluptates harum doloremque cupiditate soluta!
              </MetadataListTextItem>
              <MetadataListTextItem title={t("menu:title.primaryText")} color="primary.text">
                {t("common:text")}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("menu:title.successText")} color="success">
                {t("common:text")}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("menu:title.warningText")} color="warning">
                {t("common:text")}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("menu:title.errorText")} color="error">
                {t("common:text")}
              </MetadataListTextItem>
              <MetadataListTextItem title={t("menu:title.infoText")} color="info">
                {t("common:text")}
              </MetadataListTextItem>
              <MetadataListTextItem
                title={`${t("common:placeholderUpperCase")} / ${t("menu:title.emptyValueText")}`}
                color="text.text"
              >
                N/A
              </MetadataListTextItem>
              <MetadataListItem title={t("common:link")}>
                <Link underline="hover" color="primary.text">
                  {t("common:link")}
                </Link>
              </MetadataListItem>
              <MetadataListItem title={t("common:chip")}>
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
              </MetadataListItem>
              <MetadataListChipListItem title={t("menu:title.lastChip")}>
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
                <Chip label={t("common:chip")} color="primary" variant="outlined" />
              </MetadataListChipListItem>
              <MetadataListItem title={t("common:button")}>
                <Button color="primary" variant="outlined">
                  {t("common:button")}
                </Button>
              </MetadataListItem>

              <MetadataListItem title={t("menu:title.anyContent")}>{t("menu:item.anyContent")}</MetadataListItem>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ mt: 4, mb: 4, p: 2, background: "none" }}>
            <h1 id="metadata-accordions">{t("page:body.metadataListAccordions")}</h1>
            <Box mt={3} mb={2}>
              <MetadataBoxAccordion
                title={t("page:metadataBoxAccordion.title.contentDetails")}
                icon={<FormatListBulletedIcon />}
                rightContent={
                  <Button variant="outlined" color="primary">
                    {t("page:body.rightContent")}
                  </Button>
                }
              >
                <Stack direction="row" spacing={2}>
                  <Stack flex={1} spacing={1}>
                    <MetadataListChipListItem title={t("menu:title.chipList")} spaced>
                      <Chip size="small" label={t("chip:label.tagWithNumber", { number: 1 })} />
                      <Chip size="small" color="primary" label={t("chip:label.tagWithNumber", { number: 2 })} />
                      <Chip size="small" color="success" label={t("chip:label.tagWithNumber", { number: 3 })} />
                    </MetadataListChipListItem>
                    <MetadataListItem title={t("common:chip")} spaced>
                      <Chip size="small" label={t("chip:label.tagWithNumber", { number: 1 })} />
                    </MetadataListItem>
                    <MetadataListTextItem title={t("menu:title.textItem")} spaced>
                      {t("menu:item.textItem")}
                    </MetadataListTextItem>
                  </Stack>
                  <Stack flex={1} spacing={1}>
                    <MetadataListChipListItem title={t("menu:title.chipList")} spaced>
                      <Chip size="small" label={t("chip:label.tagWithNumber", { number: 1 })} />
                      <Chip size="small" color="primary" label={t("chip:label.tagWithNumber", { number: 2 })} />
                      <Chip size="small" color="success" label={t("chip:label.tagWithNumber", { number: 3 })} />
                    </MetadataListChipListItem>
                    <MetadataListItem title={t("common:chip")} spaced>
                      <Chip size="small" label={t("chip:label.tagWithNumber", { number: 1 })} />
                    </MetadataListItem>
                    <MetadataListTextItem
                      title={t("menu:title.textItem")}
                      wrapperProps={{ alignItems: "flex-start" }}
                      spaced
                    >
                      Lorem ipsum dolor sit amet consectetur adipisicing elit. Quod, culpa assumenda. Maiores, quisquam
                      aspernatur. Eaque beatae ad eligendi doloremque earum molestiae ipsa facilis, fuga architecto
                      libero rem? Maxime, officia reiciendis.
                    </MetadataListTextItem>
                  </Stack>
                </Stack>
              </MetadataBoxAccordion>
            </Box>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <MetadataBoxAccordion
                  title={t("page:metadataBoxAccordion.title.contentDetails")}
                  icon={<FormatListBulletedIcon />}
                >
                  <Stack spacing={1}>
                    <MetadataListTextItem title={t("menu:title.textItem")}>
                      {t("menu:item.textItem")}
                    </MetadataListTextItem>
                    <MetadataListTextItem title={t("menu:title.textItem")}>
                      {t("menu:item.textItem")}
                    </MetadataListTextItem>
                    <MetadataListChipListItem title={t("menu:title.chipList")}>
                      <Chip size="small" label={t("chip:label.tagWithNumber", { number: 1 })} />
                      <Chip size="small" color="primary" label={t("chip:label.tagWithNumber", { number: 2 })} />
                      <Chip size="small" color="success" label={t("chip:label.tagWithNumber", { number: 3 })} />
                    </MetadataListChipListItem>
                  </Stack>
                </MetadataBoxAccordion>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <h1 id="activities">{t("common:activities")}</h1>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
                <h2 id="activities-heading">{t("page:activitiesHeading")}</h2>
                <Stack spacing={1}>
                  <ActivityHeading
                    variant="warning"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="error"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.aBlockedConfiguration")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="primary"
                    action={t("page:activityHeading.action.created")}
                    label={t("page:activityHeading.label.aTitle")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="success"
                    action={t("page:activityHeading.action.created")}
                    label={t("page:activityHeading.label.aTitle")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="secondary"
                    action={t("page:activityHeading.action.created")}
                    label={t("page:activityHeading.label.aTitle")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="info"
                    action={t("page:activityHeading.action.created")}
                    label={t("page:activityHeading.label.aTitle")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />

                  <ActivityHeading
                    variant="warning"
                    label={t("page:activityHeading.label.configurationBlocked")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="error"
                    label={t("page:activityHeading.label.configurationDeleted")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="primary"
                    label={t("page:activityHeading.label.orderCreated")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="success"
                    label={t("page:activityHeading.label.configurationDelivered")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                  <ActivityHeading
                    variant="info"
                    label={t("page:activityHeading.label.configurationUpdated")}
                    userName="Test"
                    userInitials="T"
                    isRead={false}
                    userId={0}
                  />
                </Stack>
                <h2 id="activities-context">{t("page:body.activitiesContext")}</h2>
                <Stack spacing={1}>
                  <ActivityContext
                    assetType={ObjectType.Audio}
                    assetLayer={ObjectLayer.Asset}
                    activityKind={ActivityKind.Asset}
                    title={t("page:activityContext.title.audioAsset")}
                    subtitle={undefined}
                    label={t("common:lowerCaseAudio")}
                    detail={t("page:activityContext.detail.audioAssetDetails")}
                    resId={0}
                  />
                  <ActivityContext
                    assetType={ObjectType.Subtitle}
                    assetLayer={ObjectLayer.Asset}
                    activityKind={ActivityKind.Asset}
                    title={t("page:activityContext.title.subtitleAsset")}
                    subtitle={undefined}
                    label={t("common:subtitle")}
                    detail={t("page:activityContext.detail.subtitleAssetDetails")}
                    resId={0}
                  />
                  <ActivityContext
                    activityKind={ActivityKind.Order}
                    title={t("page:activityContext.title.orderWithIdentifier", { identifier: "DF-864684" })}
                    subtitle={undefined}
                    label={undefined}
                    detail={t("common:orderDetails")}
                    resId={0}
                  />
                  <ActivityContext
                    activityKind={ActivityKind.Order}
                    title={t("page:activityContext.title.orderWithIdentifier", { identifier: "DF-864684" })}
                    subtitle={undefined}
                    label={undefined}
                    detail={t("common:orderDetails")}
                    resId={0}
                  />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
                <h2 id="activities-details">{t("page:body.activitiesDetails")}</h2>
                <h3>{t("common:comment")}</h3>
                <ActivityComment>Lorem ipsum</ActivityComment>
                <h3>{t("common:field")}</h3>
                <ActivityField>
                  Morbi aliquet sem sem, nec dapibus libero mattis tincidunt. Sed rhoncus ligula vel tristique
                  facilisis. Duis quis massa ullamcorper, auctor lacus nec, efficitur odio. Morbi quis leo vel lacus...
                </ActivityField>
                <h3>{t("common:upperCaseError")}</h3>
                <ActivityError label={t("common:rejected")}>
                  {t("page:activityError.body.offsetNeeded", {
                    frames: 10,
                    ref: 453163,
                    fps: 25,
                    time: "TC 12:05:36:00",
                  })}
                </ActivityError>
                <h3>{t("common:update")}</h3>
                <Stack spacing={0.5}>
                  <ActivityUpdate
                    text="4K HDR, 24 FPS"
                    icon={<TvIcon />}
                    label={t("page:activityUpdate.label.missingAsset")}
                    variant="warning"
                  />
                  <ActivityUpdate
                    text="4K HDR, 24 FPS"
                    icon={<TvIcon />}
                    label={t("page:activityUpdate.label.updated")}
                    variant="info"
                  />
                  <ActivityUpdate
                    text="S2: Stranger Things, Episode 1"
                    label={t("common:delivered")}
                    labelSecondary={t("common:feature")}
                    variant="success"
                  />
                  <ActivityUpdate
                    text="ProxyTag_34724.mp4"
                    label={t("common:added")}
                    link="https://"
                    variant="primary"
                  />
                </Stack>

                <h2 id="activities-conversation">{t("page:body.activitiesConversation")}</h2>
                <Stack spacing={0.5}>
                  <ActivityConversation
                    variant="info"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                  />
                  <ActivityConversation
                    variant="primary"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                  />
                  <ActivityConversation
                    variant="success"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                  />
                  <ActivityConversation
                    variant="warning"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                  />
                  <ActivityConversation
                    variant="error"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                  />
                  <ActivityConversation
                    variant="info"
                    action={t("page:activityHeading.action.orderHas")}
                    label={t("page:activityHeading.label.missingItem")}
                    user="test"
                    body="Order DF-220715-37850"
                    selected
                  />
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
                <h2 id="activities-feed">{t("page:body.feed")}</h2>
                <Stack spacing={4}>
                  <Box>
                    <ActivityHeading
                      variant="secondary"
                      action={t("page:activityHeading.action.reply")}
                      label={t("page:activityHeading.label.toAConversation")}
                      userName="Test"
                      userInitials="T"
                      isRead={false}
                      userId={0}
                    />
                    <ActivityDetails>
                      <ActivityCommentFeed activities={[]} myUserId={1} parentActivityId={-1} />
                    </ActivityDetails>
                  </Box>
                  <Box>
                    <ActivityHeading
                      variant="secondary"
                      action={t("page:activityHeading.action.reply")}
                      label={t("page:activityHeading.label.toAConversation")}
                      userName="Test"
                      userInitials="T"
                      isRead={false}
                      userId={0}
                    />
                    <ActivityDetails>
                      <ActivityContext
                        title="S2: Stranger Things"
                        assetType={ObjectType.Video}
                        assetLayer={ObjectLayer.Asset}
                        activityKind={ActivityKind.Asset}
                        detail="DREAMERS WITHIN THE DREAM : LA GENÈSE DE TOTAL RECALL / Mezz - French - Full Subtitles..."
                        label={t("common:trailer")}
                        resId={0}
                      />
                      <ActivityError label={t("common:rejected")}>
                        {t("page:activityError.body.offsetNeeded", {
                          frames: 10,
                          ref: 453163,
                          fps: 25,
                          time: "TC 12:05:36:00",
                        })}
                      </ActivityError>
                    </ActivityDetails>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 4, display: "flex", flexDirection: "column", backgroundImage: "none" }}>
            <h1 id="advanced-queries">{t("page:body.advancedQueries")}</h1>
            <h2 id="advanced-queries-saved">{t("page:body.advancedQuerySaved")}</h2>
            {/*<Box width={600}>
              <AdvancedQuerySaved
                title="All american titles post-1990"
                tags={["Titles", "No cast", "Not series", "> 1990", "No poster", "Other", "Other", "Other"]}
                id={0}
              />
                        </Box>*/}
            <h2 id="advanced-queries-queries">{t("common:advancedQueries")}</h2>
            <AdvancedQueries
              queries={[
                { parenthesis: "(" },
                { start: "Type", condition: "is", end: "Asset", color: "success" },
                { operator: "and" },
                { start: "Type", condition: "is", end: "Asset", color: "success" },
                { parenthesis: "(" },
                { operator: "or" },
                { start: "Type", condition: "is not", end: "Asset", color: "error" },
                { operator: "or" },
                { start: "Type", condition: "is not", end: "Asset", color: "error" },
                { operator: "and" },
                { start: "Type", condition: "is not", end: "Asset", color: "error" },
              ]}
            />
            <Box width={300}>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <AdvancedQueries
                queries={[
                  { start: "Type", condition: "is", end: "Asset", color: "success" },
                  { operator: "and" },
                  { start: "Type", condition: "is", end: "Asset", color: "success" },
                  { operator: "or" },
                  { start: "Type", condition: "is not", end: "Asset", color: "error" },
                  { operator: "or" },
                  { start: "Type", condition: "is not", end: "Asset", color: "error" },
                  { operator: "and" },
                  { start: "Type", condition: "is not", end: "Asset", color: "error" },
                ]}
              />
            </Box>
            <h2 id="advanced-queries-tag">{t("page:body.advancedQueryTag")}</h2>
            <Stack alignItems="flex-start" spacing={1}>
              <AdvancedQueryTag start="Type" condition="is" end="Asset" color="success" />
              <AdvancedQueryTag start="Type" condition="is not" end="Asset" color="error" />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ mt: 4, mb: 4, p: 2 }}>
            <h1 id="empty-slot-list">{t("page:body.emptySlotList")}</h1>
            <EmptyListSlot
              title={t("component:emptyListSlot.title.itIsEmptyHere")}
              icon={<BookmarksOutlinedIcon />}
              desc={t("component:emptyListSlot.desc.searchForContent")}
            />
          </Paper>
          <Paper sx={{ mt: 4, mb: 4, p: 2, backgroundImage: "none" }}>
            <h1 id="modals">{t("page:body.modals")}</h1>
            <ModalRenameOpenButton />
            <Divider sx={{ mt: 4, mb: 4 }} />
            <ModalRenameCollection closeModal={() => {}} />
            <h2 id="modals-onboarding">{t("page:body.modalsOnboarding")}</h2>
            <ModalOnboarding closeModal={() => {}} />
            <h2 id="modals-upload-media-files">{t("page:body.modalsUploadMediaFiles")}</h2>
            <ModalUploadMediaFiles objectId={1} objectType={ObjectType.Unknown} closeModal={() => {}} />
          </Paper>
          <Paper sx={{ mt: 4, mb: 4, p: 2, backgroundImage: "none" }}>
            <h1 id="tutorials">{t("page:body.tutorials")}</h1>
            <Button onClick={handleHelpClick}>{t("button:openHelpPopup")}</Button>
            {helpPopupAnchorEl && (
              <HelpPopup visible={Boolean(helpPopupAnchorEl)} onClose={onClosePopup} anchorEl={helpPopupAnchorEl} />
            )}
            <Divider sx={{ mt: 4, mb: 4 }} />
            <Stack alignItems="flex-start">
              <Typography id="tutorial-uikit-demo">{t("page:body.uiKitTutorialDemoAnchor")}</Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, backgroundImage: "none" }}>
            <h1 id="panels">{t("page:body.panels")}</h1>
            <Stack spacing={2} sx={{ mb: 2, width: 700 }}>
              <h2 id="panel-header">{t("page:body.panelHeader")}</h2>
              <PanelHeader
                title={t("page:panelHeader.title.panelHeaderTitle")}
                leftContent={<span>{t("page:panelHeader.leftContent.leftContent")}</span>}
                rightContent={<span>{t("page:body.rightContent")}</span>}
              />
              <PanelHeader
                title={`${t("page:panelHeader.title.panelHeaderTitle")} - ${t("page:panelHeader.title.left")}`}
                leftContent={<span>{t("page:panelHeader.leftContent.leftContent")}</span>}
                rightContent={<span>{t("page:body.rightContent")}</span>}
                placement="left"
              />
              <PanelHeader
                title={`${t("page:panelHeader.title.panelHeaderTitle")} - ${t("page:panelHeader.title.nested")}`}
                leftContent={<span>{t("page:panelHeader.leftContent.leftContent")}</span>}
                rightContent={<span>{t("page:body.rightContent")}</span>}
                opening="nested"
              />
              <PanelHeader
                title={`${t("page:panelHeader.title.panelHeaderTitle")} - ${t("page:panelHeader.title.transparent")}`}
                leftContent={<span>{t("page:panelHeader.leftContent.leftContent")}</span>}
                rightContent={<span>{t("page:body.rightContent")}</span>}
                type="transparent"
              />
            </Stack>
            <h2 id="panel-list">{t("page:body.panelsList")}</h2>
            <Stack spacing={2} direction="row" flexWrap="wrap">
              <Button onClick={toggleProcessingAssetPanel}>{t("button:openProcessingAssetPanel")}</Button>
              <Button onClick={toggleRejectedAssetPanelOpen}>{t("button:openRejectedAssetPanel")}</Button>
              <Button onClick={toggleShareDetailsPanelOpen}>{t("button:openShareDetailsPanel")}</Button>
              <Button onClick={toggleOrderPanelOpen}>{t("button:openOrderPanel")}</Button>
              <Button onClick={toggleCastingPanelOpen}>{t("button:openCastingPanel")}</Button>
              <Button onClick={toggleConfigPanelOpen}>{t("button:openConfigPanel")}</Button>
              <Button onClick={toggleSynopsisPanelOpen}>{t("button:openSynopsisPanel")}</Button>
              <Button onClick={toggleNewRequestPanel}>{t("button:openNewRequestPanel")}</Button>
            </Stack>
            <h3>{t("common:customer")}</h3>
            <Stack spacing={2} direction="row" flexWrap="wrap">
              <Button onClick={toggleCustomerDetailsPanel}>{t("button:openCustomerDetailsPanel")}</Button>
              <Button onClick={toggleCustomerEditUserPanel}>{t("button:openNewCustomerDetailsPanel")}</Button>
              <Button onClick={toggleCustomerUsersPanel}>
                {t("button:openCustomerUser")} - {t("button:detailsPanel")}
              </Button>
            </Stack>
            <h3>{t("common:provider")}</h3>
            <Stack spacing={2} direction="row" flexWrap="wrap">
              <Button onClick={toggleProviderDetailsPanel}>{t("button:openProviderDetailsPanel")}</Button>
              <Button onClick={toggleCustomerUsersPanel}>
                {t("button:openProviderUser")} - {t("button:detailsPanel")}
              </Button>
            </Stack>
            <h3>{t("common:user")}</h3>
            <Stack spacing={2} direction="row" flexWrap="wrap">
              <Button onClick={toggleEditUserRolePanel}>{t("button:openEditUserRolePanel")}</Button>
              <Button onClick={toggleGroupUserPanel}>{t("button:openGroupUserPanel")}</Button>
            </Stack>

            <AssetPanel
              assetId={925457}
              open={processingAssetPanelOpen}
              onClose={toggleProcessingAssetPanel}
              closePanel={toggleRejectedAssetPanelOpen}
            />
            {/* <AssetPanel
              assetId={925457}
              open={rejectedAssetPanelOpen}
              onClose={toggleRejectedAssetPanelOpen}
            />  */}
            {/* <ShareDetailsPanel open={shareDetailsPanelOpen} onClose={toggleShareDetailsPanelOpen} /> */}
            <CastingPanel cast={{ Cast: ["Cast"] }} open={castingPanelOpen} onClose={toggleCastingPanelOpen} />
            <OrderItemPanel open={configPanelOpen} onClose={toggleConfigPanelOpen} itemId={14556} />
            <OrderPanel open={orderPanelOpen} onClose={toggleOrderPanelOpen} orderId={0} />
            <NewRequestPanel type="create" open={newRequestPanelOpen} onClose={toggleNewRequestPanel} />
            <SynopsisPanel
              open={synopsisPanelOpen}
              onClose={toggleSynopsisPanelOpen}
              list={[
                {
                  languageCode: { value: "test", label: "test", id: 0, externalId: 23, rfc5646: "fr-fr" },
                  languageLocalized: null,
                  text: "Lorem ipsum",
                },
              ]}
            />
            {/* <ProvidersDetailPanel open={providerDetailsPanelOpen} onClose={toggleProviderDetailsPanel} /> */}
            {/* <CustomerDetailsPanel open={customerDetailsPanelOpen} onClose={toggleCustomerDetailsPanel} /> */}
            <CustomerUsersPanel open={customerUsersPanel} onClose={toggleCustomerUsersPanel} users={[]} />
            {/*<CustomerEditUserPanel open={customerEditUserPanelOpen} onClose={toggleCustomerEditUserPanel} />*/}
            {/*<EditUserRolePanel open={editUserRolePanelOpen} onClose={toggleEditUserRolePanel} role={null} />*/}
            {/*<GroupUserPanel open={groupUserPanelOpen} onClose={toggleGroupUserPanel} groupId={10} />*/}
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, backgroundImage: "none" }}>
            <h1 id="filters">{t("common:filters")}</h1>
            <Box width={400}>
              <FilterAccordion title="Accordéon de Filtre" expanded={true} onExpand={() => false} nbActiveFilters={2}>
                Contenu de l'accordéon
              </FilterAccordion>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 4, backgroundImage: "none" }}>
            <h1 id="misc">{t("common:misc")}</h1>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <h2 id="misc-comment">{t("common:comment")}</h2>
                <CommentForm
                  onSend={(comment) => {
                    console.log(`Send comment : ${comment}`);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <h2 id="misc-priority-select">{t("page:body.priotirySelect")}</h2>
                <PrioritySelect
                  value={priority}
                  onChange={(value) => {
                    setPriority(value);
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <h2 id="misc-config-content-recaps">{t("page:body.configContentRecaps")}</h2>
                <ConfigContentRecaps
                  setting={t("common:quality")}
                  voice={t("page:configContentRecaps.voice.voices")}
                  subtitles={t("common:subtitles")}
                  transfert={t("page:configContentRecaps.transfert.transfert")}
                  collection={t("common:collection")}
                />
                <ConfigContentRecaps
                  setting={t("common:quality")}
                  voice={t("page:configContentRecaps.voice.voices")}
                  subtitles={t("common:subtitles")}
                  transfert={t("page:configContentRecaps.transfert.transfert")}
                  collection={t("common:collection")}
                  color="primary.text"
                />
              </Grid>
              <Grid item xs={4}>
                <h2 id="misc-title-item">{t("page:body.titleItem")}</h2>
                <Stack direction="row" alignItems="center" spacing={3} mb={2}>
                  <TitleItem
                    poster="/static/images/temp/movie-cover-1.jpg"
                    title="Stranger Things"
                    size="small"
                    desc={t("common:description")}
                  />
                  <TitleItem
                    poster="/static/images/temp/movie-cover-1.jpg"
                    title={t("page:titleItem.title.largeTitle")}
                    size="large"
                    desc={t("common:description")}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={3} mb={2}>
                  <TitleItem poster="/static/images/temp/movie-cover-1.jpg" title="Stranger Things" size="small">
                    <AssetTypeChip size="small" uncolored type={ObjectType.Video} />
                  </TitleItem>
                  <TitleItem poster="/static/images/temp/movie-cover-1.jpg" title="Stranger Things" size="small">
                    <AssetTypeChip size="small" uncolored type={ObjectType.Subtitle} />
                  </TitleItem>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={3} mb={2}>
                  <TitleItem poster="/static/images/temp/movie-cover-1.jpg" title="Stranger Things" size="small">
                    <Link>{t("button:otherContent")}</Link>
                  </TitleItem>
                </Stack>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2 }}>
                  <h2 id="misc-box-paper">{t("page:body.boxPaper")}</h2>
                  <BoxPaper title={t("common:title")} rightContent={<Button size="small">{t("common:action")}</Button>}>
                    {t("common:content")}
                  </BoxPaper>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mt: 4, backgroundImage: "none" }}>
              <h1 id="note">{t("common:note")}</h1>
              <Grid container spacing={3}>
                <Grid item lg={2}>
                  <h2 id="note-non-editable">{t("page:body.nonEditableNote")}</h2>
                  <Note
                    title={t("page:note.title.defaultSetupForStudioCanal")}
                    desc="DCP StudioCanal (JohnWick4 + EN/FR/DE 4K HDR) DCP StudioCanal (JohnWick4 + EN/FR/DE 4K HDR)"
                  />
                </Grid>
                <Grid item lg={2}>
                  <h2 id="note-editable">{t("page:body.editableNote")}</h2>
                  <Note
                    title={t("page:note.title.defaultSetupForStudioCanal")}
                    desc="DCP StudioCanal (JohnWick4 + EN/FR/DE 4K HDR) DCP StudioCanal (JohnWick4 + EN/FR/DE 4K HDR)"
                    editable
                  />
                </Grid>
                <Grid item lg={2}>
                  <h2 id="note-empty-editable">{t("page:body.emptyEditableNote")}</h2>
                  <Note editable />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Paper sx={{ p: 2, mt: 4, backgroundImage: "none" }}>
            <h1 id="page-filters">{t("page:body.pageFilters")}</h1>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={3}>
                <PageFilter variant="success" title="48" subtitle={t("common:delivered")} icon={<CheckIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter variant="primary" title="1230" subtitle={t("common:processing")} icon={<CachedIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter variant="info" title="1230" subtitle={t("common:processing")} icon={<CachedIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  variant="background"
                  title="1230"
                  subtitle={t("common:pending")}
                  icon={<HourglassEmptyIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  variant="warning"
                  title="1230"
                  subtitle={t("common:fixingUp")}
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  variant="error"
                  title="1230"
                  subtitle={t("common:errors")}
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={3}>
                <PageFilter active variant="success" title="48" subtitle={t("common:delivered")} icon={<CheckIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  active
                  variant="primary"
                  title="1230"
                  subtitle={t("common:processing")}
                  icon={<CachedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  active
                  variant="info"
                  title="1230"
                  subtitle={t("common:processing")}
                  icon={<CachedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  active
                  variant="background"
                  title="1230"
                  subtitle={t("common:pending")}
                  icon={<HourglassEmptyIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  active
                  variant="warning"
                  title="1230"
                  subtitle="Fixing up"
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  active
                  variant="error"
                  title="1230"
                  subtitle={t("common:errors")}
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <PageFilter small variant="success" title="48" subtitle={t("common:delivered")} icon={<CheckIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  small
                  variant="primary"
                  title="1230"
                  subtitle={t("common:processing")}
                  icon={<CachedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter small variant="info" title="1230" subtitle={t("common:processing")} icon={<CachedIcon />} />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  small
                  variant="background"
                  title="1230"
                  subtitle={t("common:pending")}
                  icon={<HourglassEmptyIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  small
                  variant="warning"
                  title="1230"
                  subtitle={t("common:fixingUp")}
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={3}>
                <PageFilter
                  small
                  variant="error"
                  title="1230"
                  subtitle={t("common:errors")}
                  icon={<SettingsOutlinedIcon />}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}

const ModalRenameOpenButton = () => {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleRenameClick: React.MouseEventHandler = () => {
    if (!openModal) {
      return;
    }

    const onClose: ModalRenameElementProps["onClose"] = (data) => {
      console.log(data);
    };

    openModal<ModalRenameElementProps>(ModalRenameCollection, {
      onClose,
    });
  };

  return (
    <Button variant="icon" onClick={handleRenameClick} startIcon={<EditOutlinedIcon />}>
      {t("button:openModal")}
    </Button>
  );
};

export default DemoSamples;
